/**
 * "O que fazer agora" da biblioteca de modelos: sugestoes derivadas dos
 * numeros, cada uma com UMA acao que a pagina sabe executar.
 *
 * Ordem de urgencia (prioridade menor = mais urgente):
 *  1. Toque da regua parado: modelo ligado a uma regua que nao esta aprovado
 *     (pendente ou rejeitado). Enquanto isso o toque nao sai para ninguem, e
 *     toque que nao sai nao recupera.
 *  2. Modelo rejeitado (fora da regua): o motivo da Meta e a correcao.
 *  3. Queda forte de pagamentos contra o periodo anterior (as 2 maiores).
 *  4. Diferenca grande dentro da mesma categoria: comparar lado a lado e levar
 *     o que funciona para o outro.
 *  5. Muita resposta e pouca venda com a vendedora: o gargalo e o atendimento
 *     (os 2 com mais respostas).
 *  6. Pendentes ha mais de 24 h que NAO estao numa regua (os da regua ja
 *     entraram no item 1).
 *  7. Modelo pronto e sem uso numa regua.
 *
 * Devolve TODAS em ordem; quem mostra decide quantas cabem (o componente
 * mostra quatro e abre o resto em "ver mais").
 *
 * Cada titulo carrega o numero que o justifica, e o texto diz de onde veio.
 * Nenhuma sugestao propoe falar com alguem ANTES do fato.
 */

import type { BadgeTone, IconName } from '../../components/ui';
import { formatCount, formatDate, formatPercentFromBps } from '../../lib/format';
import { chaveDoGrupo, compararDesempenho, elegivelAoRanking, geraCredito, rotuloDoGrupo, taxaBps, taxaPagamentoBps, variar } from './desempenho';
import type { FiltrosLocais } from './filtragem';
import type { MessageTemplate } from './types';
import { abaDoCanal, contar } from './vocabulario';

export type AbaDaPrevia = 'previa' | 'desempenho' | 'historico';

export type AcaoInsight =
  | { readonly kind: 'editar'; readonly id: string }
  | { readonly kind: 'previa'; readonly id: string; readonly aba: AbaDaPrevia }
  | { readonly kind: 'comparar'; readonly ids: readonly [string, string] }
  | { readonly kind: 'filtrar'; readonly filtros: Partial<FiltrosLocais> };

export interface InsightModelo {
  readonly id: string;
  readonly prioridade: number;
  readonly tone: BadgeTone;
  readonly icone: IconName;
  readonly titulo: string;
  readonly texto: string;
  readonly rotuloAcao: string;
  readonly acao: AcaoInsight;
}

const DIA_MS = 86_400_000;

function naRegua(t: MessageTemplate): string | null {
  return t.usedIn.find((u) => u.startsWith('Régua')) ?? null;
}

export function gerarInsights(lista: readonly MessageTemplate[], agoraMs: number): readonly InsightModelo[] {
  const insights: InsightModelo[] = [];

  // 1 e 2. Parados e rejeitados
  for (const t of lista) {
    const regua = naRegua(t);
    if (regua !== null && (t.status === 'pendente' || t.status === 'rejeitado')) {
      insights.push({
        id: `parado-${t.id}`,
        prioridade: 1,
        tone: 'critical',
        icone: 'alerta',
        titulo: `${regua.split(' · ')[0] ?? regua} está parada: ${t.name} ${t.status === 'pendente' ? 'aguarda a Meta' : 'foi recusado'}`,
        texto:
          t.status === 'pendente'
            ? `Enviado em ${formatDate(t.updatedAt)}. Enquanto a Meta não aprovar, esse toque não sai para ninguém — e toque que não sai não recupera. Se passar de 24 h, reenvie ou troque por um modelo aprovado.`
            : `${t.statusNote ?? 'Recusado pela Meta.'} Enquanto isso, esse toque não sai para ninguém.`,
        rotuloAcao: t.status === 'pendente' ? 'Ver o modelo' : 'Corrigir no editor',
        acao: t.status === 'pendente' ? { kind: 'previa', id: t.id, aba: 'previa' } : { kind: 'editar', id: t.id },
      });
    } else if (t.status === 'rejeitado') {
      insights.push({
        id: `rejeitado-${t.id}`,
        prioridade: 2,
        tone: 'warning',
        icone: 'alerta',
        titulo: `${t.name} foi recusado pela Meta`,
        texto: t.statusNote ?? 'Corrija o texto e reenvie.',
        rotuloAcao: 'Corrigir no editor',
        acao: { kind: 'editar', id: t.id },
      });
    }
  }

  // 3. Pendentes ha mais de 24 h
  const atrasados = lista.filter((t) => t.status === 'pendente' && naRegua(t) === null && agoraMs - new Date(t.updatedAt).getTime() > DIA_MS);
  if (atrasados.length > 0) {
    insights.push({
      id: 'pendentes-24h',
      prioridade: 6,
      tone: 'warning',
      icone: 'relogio',
      titulo: `${contar(atrasados.length, 'modelo aguarda', 'modelos aguardam')} a Meta há mais de 24 h`,
      texto: `${atrasados.map((t) => t.name).slice(0, 3).join(', ')}${atrasados.length > 3 ? '…' : ''}. A análise costuma sair antes disso; confira se a categoria está certa e reenvie.`,
      rotuloAcao: 'Ver pendentes',
      acao: { kind: 'filtrar', filtros: { tab: 'whatsapp', status: 'pendente', publico: 'todos', categoria: 'todas', setor: 'todos', uso: 'todos', busca: '' } },
    });
  }

  // 3. Queda forte de pagamentos (as duas maiores)
  const quedas = lista
    .map((t) => ({ t, anterior: t.previousPerformance, v: t.previousPerformance === null ? null : variar(t.performance.paid, t.previousPerformance.paid) }))
    .filter((q) => q.anterior !== null && q.anterior.paid >= 5 && q.v !== null && q.v.direcao === 'queda' && q.v.relativaBps !== null && q.v.relativaBps <= -3_000)
    .sort((x, y) => (x.v?.relativaBps ?? 0) - (y.v?.relativaBps ?? 0))
    .slice(0, 2);
  for (const { t, anterior, v } of quedas) {
    if (anterior !== null && v !== null && v.relativaBps !== null) {
      insights.push({
        id: `queda-${t.id}`,
        prioridade: 3,
        tone: 'warning',
        icone: 'grafico',
        titulo: `Pagamentos pelo ${t.name} caíram ${formatPercentFromBps(Math.abs(v.relativaBps))}`,
        texto: `De ${contar(anterior.paid, 'pagamento', 'pagamentos')} para ${formatCount(t.performance.paid)} contra o período anterior de mesmo tamanho. Veja se a mudança de texto ou o público explicam a queda.`,
        rotuloAcao: 'Ver desempenho',
        acao: { kind: 'previa', id: t.id, aba: 'desempenho' },
      });
    }
  }

  // 5. Muita resposta, pouca venda com a vendedora (so publicos que creditam: o resto e da propria vendedora)
  const gargalos = lista
    .filter((t) => {
      const fechouBps = taxaBps(t.performance.paidViaSeller, t.performance.replied);
      return geraCredito(t) && t.performance.replied >= 20 && fechouBps !== null && fechouBps < 1_000;
    })
    .sort((x, y) => y.performance.replied - x.performance.replied)
    .slice(0, 2);
  for (const t of gargalos) {
    const p = t.performance;
    insights.push({
      id: `resposta-${t.id}`,
      prioridade: 5,
      tone: 'accent',
      icone: 'resposta',
      titulo: `${contar(p.replied, 'pessoa respondeu', 'pessoas responderam')} ao ${t.name}; só ${contar(p.paidViaSeller, 'fechou', 'fecharam')} com a vendedora`,
      texto: `A mensagem funciona (${formatPercentFromBps(taxaBps(p.replied, p.delivered) ?? 0)} de quem recebeu respondeu); quem responde cai com a vendedora e a régua para. O gargalo está no atendimento do comercial, não no texto: veja a fila na tela Comercial da empresa.`,
      rotuloAcao: 'Ver desempenho',
      acao: { kind: 'previa', id: t.id, aba: 'desempenho' },
    });
  }

  // 4. Maior diferenca dentro da mesma categoria
  const grupos = new Map<string, MessageTemplate[]>();
  for (const t of lista.filter(elegivelAoRanking)) {
    const chave = chaveDoGrupo(t);
    grupos.set(chave, [...(grupos.get(chave) ?? []), t]);
  }
  let melhorPar: { readonly a: MessageTemplate; readonly b: MessageTemplate; readonly ganhoBps: number } | null = null;
  for (const grupo of grupos.values()) {
    if (grupo.length < 2) {
      continue;
    }
    const ordenado = [...grupo].sort(compararDesempenho);
    const a = ordenado[0];
    const b = ordenado[ordenado.length - 1];
    const ta = a === undefined ? null : taxaPagamentoBps(a.performance);
    const tb = b === undefined ? null : taxaPagamentoBps(b.performance);
    if (a === undefined || b === undefined || ta === null || tb === null || tb <= 0) {
      continue;
    }
    const ganhoBps = Math.round(((ta - tb) * 10_000) / tb);
    if (ganhoBps >= 5_000 && (melhorPar === null || ganhoBps > melhorPar.ganhoBps)) {
      melhorPar = { a, b, ganhoBps };
    }
  }
  if (melhorPar !== null) {
    const { a, b, ganhoBps } = melhorPar;
    insights.push({
      id: `comparar-${a.id}-${b.id}`,
      prioridade: 4,
      tone: 'brand',
      icone: 'estrela',
      titulo: `${a.name} converte ${formatPercentFromBps(ganhoBps)} mais que ${b.name}`,
      texto: `Mesma categoria (${rotuloDoGrupo(a)}): ${formatPercentFromBps(taxaPagamentoBps(a.performance) ?? 0)} contra ${formatPercentFromBps(taxaPagamentoBps(b.performance) ?? 0)} de quem recebeu pagaram. Compare lado a lado e leve o que funciona para o outro.`,
      rotuloAcao: 'Comparar lado a lado',
      acao: { kind: 'comparar', ids: [a.id, b.id] },
    });
  }

  // 7. Pronto e sem uso
  const semUso = lista.filter((t) => t.sector === 'funil' && t.usedIn.length === 0 && (t.status === 'aprovado' || t.status === 'texto_livre'));
  if (semUso.length > 0) {
    const primeiro = semUso[0];
    insights.push({
      id: 'sem-uso',
      prioridade: 7,
      tone: 'info',
      icone: 'raio',
      titulo:
        semUso.length === 1 && primeiro !== undefined
          ? `${primeiro.name} está pronto e não está ligado a nenhuma régua`
          : `${contar(semUso.length, 'modelo do funil está pronto', 'modelos do funil estão prontos')} e sem uso`,
      texto: 'Modelo parado não recupera ninguém. Ligue-o a um toque da régua (sempre depois do abandono ou do vencimento) ou arquive para limpar a biblioteca.',
      rotuloAcao: 'Ver modelos sem uso',
      acao: { kind: 'filtrar', filtros: { tab: primeiro === undefined ? 'whatsapp' : abaDoCanal(primeiro.channel), uso: 'sem_uso', status: 'todos', publico: 'todos', categoria: 'todas', setor: 'todos', busca: '' } },
    });
  }

  return insights.sort((x, y) => x.prioridade - y.prioridade);
}
